import { IconProps } from '@honeybook/icons';
import React from 'react';
import styled, { css } from 'styled-components';
import { fontSizes } from '../../styles/fonts';
import spacing from '../../styles/spacing';
import { TSize } from '../../tokens/types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { vars } from '../../theme';

export type ExtendedHBBadgeTSize = 'xs' | Extract<TSize, 'sm'>;

export type HBBadgeBaseProps = {
    label?: string;
    /** Background color or CSS gradient for the badge. */
    bg: string;
    color: string;
    size?: ExtendedHBBadgeTSize;
    icon?: (props: IconProps) => JSX.Element;
    /** Border color or CSS gradient (e.g. `linear-gradient(...)`) rendered via a pseudo-element to support gradient borders. */
    border?: string;
} & Partial<MustIncludeOneOfBasicTestIdProps>;

const SIZE: Record<
    ExtendedHBBadgeTSize,
    {
        height: string;
        fontSize: string;
        borderRadius: string;
        lineHeight: string;
    }
> = {
    xs: {
        height: '14px',
        fontSize: fontSizes.s11,
        borderRadius: '2px',
        lineHeight: '14px'
    },
    sm: {
        height: '20px',
        fontSize: fontSizes.s12,
        borderRadius: '2px',
        lineHeight: '20px'
    }
};

const HBBadgeBase: React.FC<HBBadgeBaseProps> = ({
    icon: Icon,
    size = 'sm',
    label,
    testId,
    testIdName,
    ...props
}) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'badge',
        componentName: 'HBBadgeBase'
    });

    return (
        <Badge size={size} {...props} data-testid={finalTestId}>
            {Icon && <Icon color={props.color} />}
            {label}
        </Badge>
    );
};

export default HBBadgeBase;

const getBorderStyles = ({
    border,
    borderRadius
}: {
    border: string;
    borderRadius: string;
}) => css`
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: ${borderRadius};
        padding: 1px;
        background: ${border};
        -webkit-mask: linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        -webkit-mask-composite: destination-out;
        mask-composite: exclude;
    }
`;

const Badge = styled.div<HBBadgeBaseProps>`
    ${props => SIZE[props.size]}
    background: ${props => props.bg};
    color: ${props => props.color};
    display: inline-flex;
    align-items: center;
    padding: 0 ${spacing[4]};
    gap: ${spacing[4]};
    font-family: ${vars.fonts.body};
    font-weight: 600;
    position: relative;
    /*
     * Using a pseudo-element to support both solid color borders and gradient borders.
     * This allows the border prop to accept either a color value or a gradient
     * (e.g. 'linear-gradient(...)') while maintaining border-radius support.
     */
    ${props =>
        props.border &&
        getBorderStyles({
            border: props.border,
            borderRadius: SIZE[props.size].borderRadius
        })}
`;
